
import React from 'react';
import {
  Scale,
  ShieldCheck,
  Handshake,
  Briefcase,
  Users,
  Globe,
  Landmark,
  Gavel,
  CheckCircle2,
  Clock,
  Award,
  BookOpen,
  FileText,
  UserCheck,
  Heart,
  Plane,
  FileSearch,
  Lock
} from 'lucide-react';
import { Translation, PracticeArea, TrustSignal, ContentCard } from './types';

export const CONFIG = {
  phoneNumber: "202-491-5042",
  whatsappNumber: "12024915042",
  email: "contact@tirana-law.com",
  address: "932 Hungerford Drive, Suite 23A, Rockville, Maryland 20850",
  // Primary Attorney Portrait - High quality placeholder matching user's specific request
  attorneyPhoto: "/attorney-akil.jpg",
  heroImage: "https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2069&auto=format&fit=crop",
  mapsUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3101.458638407421!2d-77.14728558464875!3d39.09633857953257!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s932+Hungerford+Dr+Suite+23A%2C+Rockville%2C+MD+20850!5e0!3m2!1sen!2sus!4v1715610000000!5m2!1sen!2sus"
};

export const TRANSLATIONS: Record<string, Translation> = {
  EN: {
    topBarPhone: "Schedule a Consultation:",
    heroTitle: "Boutique Legal Advocacy in MD, DC & VA",
    heroSubtitle: "Law Offices of Akil Tirana, Esq. serves clients in the tri-State area of Washington, DC, Maryland and Virginia. We are not just lawyers; we are strategic partners in your defense in a rapidly changing legal landscape.",
    heroCtaPrimary: "SCHEDULE CONSULTATION",
    heroCtaSecondary: "OUR PRACTICES",
    trustTitle: "Why Choose Our Firm?",
    trustSubtitle: "When you hire the Law Offices of Akil Tirana, Esq., you hire a dedicated advocate licensed across 3 states: Maryland, D.C., and Virginia.",
    practiceTitle: "Our Practice Areas",
    practiceSubtitle: "Sophisticated legal expertise across Maryland, Washington D.C., and Virginia.",
    contactTitle: "Reach Our Office",
    contactSubtitle: "Based in Rockville, MD. We serve clients across the entire MD, DC, and VA region with high-stakes advocacy.",
    formName: "Full Name",
    formEmail: "Email Address",
    formPhone: "Phone Number",
    formMessage: "Case Details",
    formSubmit: "SUBMIT INQUIRY",
    formSuccess: "Message received. We will contact you shortly.",
    footerRights: "© 2026 Law Offices of Akil Tirana, Esq. All rights reserved.",
    footerTagline: "Advocacy. Integrity. Results.",
    footerDisclaimer: "ATTORNEY ADVERTISING. Licensed in MD, DC, and VA. Prior results do not guarantee a similar outcome. Services offered in English only."
  }
};

export const PRACTICE_AREAS: Record<string, PracticeArea[]> = {
  EN: [
    { id: 'immigration', title: 'Immigration', description: 'Comprehensive legal strategies for visas, spousal petitions, fiancé visas, removals, and more.', icon: 'Globe' },
    { id: 'family-law', title: 'Family Law', description: 'Compassionate handling of divorce, child support, child custody, visitation, and protective orders.', icon: 'Heart' },
    { id: 'sijs', title: 'SIJS', description: 'Special Immigration Juvenile Status for unmarried, under-21 immigrants who were abused or neglected.', icon: 'Users' },
    { id: 'traffic', title: 'Traffic Violations', description: 'Representing clients in MD, DC, and VA for DUI, DWI, and driving without a license.', icon: 'Scale' },
    { id: 'accidents', title: 'Car Accidents', description: 'Negotiating medical bills, lost wages, and pain and suffering compensation.', icon: 'Gavel' },
    { id: 'workers-comp', title: 'Workers Comp', description: 'Recovering medical care and wage replacement for employees injured on the job.', icon: 'Briefcase' },
    { id: 'employment-disc', title: 'Employment Discrimination', description: 'Wrongful termination, sexual harassment, and wage violations representation.', icon: 'UserCheck' },
    { id: 'military', title: 'Military Law', description: 'Handling UCMJ matters, courts-martial, administrative actions, and record corrections.', icon: 'ShieldCheck' },
  ]
};

export const TRUST_SIGNALS: Record<string, TrustSignal[]> = {
  EN: [
    { title: "Personal Attention", description: "You deal directly with Akil Tirana, Esq., not a junior associate.", icon: "UserCheck" },
    { title: "Licensed in 3 States", description: "Fully licensed and rooted in the legal landscape of Maryland, D.C., and Virginia.", icon: "Landmark" },
    { title: "Strategic Results", description: "A track record of high-stakes victories through meticulous preparation.", icon: "ShieldCheck" }
  ]
};

export const PAGE_CONTENT: Record<string, Record<string, { title: string, subtitle: string, cards: ContentCard[] }>> = {
  EN: {
    story: {
      title: "Our Story",
      subtitle: "A legacy of precision and personal service.",
      cards: [
        { title: "Founded on Integrity", description: "Established to provide high-level advocacy with a boutique touch, focusing on quality over volume.", icon: "CheckCircle2" },
        { title: "Tri-State Reach", description: "Licensed in Maryland, Washington D.C., and Virginia to provide comprehensive regional coverage.", icon: "Landmark" },
        { title: "One-on-One Service", description: "Our firm is structured to ensure every client has direct access to their lead counsel.", icon: "Handshake" }
      ]
    },
    team: {
      title: "Meet the Attorney",
      subtitle: "Dedicated advocacy from Akil Tirana, Esq.",
      cards: [
        {
          title: "Principal Attorney",
          description: "Akil Tirana, Esq. is a senior partner licensed in 3 jurisdictions: Maryland, Washington D.C., and Virginia, specializing in complex litigation.",
          meta: "Lead Counsel",
          icon: "UserCheck"
        }
      ]
    },
    immigration: {
      title: "Immigration",
      subtitle: "Navigating residency, visas, and defense paths.",
      cards: [
        { title: "Family-Based Immigration", description: "Visas, spousal petitions, and fiancé visas representation.", icon: "Users" },
        { title: "Employment-Based Immigration", description: "H-1B, L-1, O-1 visas, and PERM labor certification management.", icon: "Briefcase" },
        { title: "Citizenship & Naturalization", description: "Assisting with green cards and citizenship applications for a secure future.", icon: "Award" },
        { title: "Removal Defense", description: "Vigorous representation in immigration court to protect your status.", icon: "ShieldCheck" },
        { title: "Habeas Corpus", description: "Defense against removal before U.S. District Courts of MD, DC, and VA.", icon: "Scale" }
      ]
    },
    'family-law': {
      title: "Family Law",
      subtitle: "Discretion and strength in family matters.",
      cards: [
        { title: "Divorce", description: "Legal guidance through marital dissolution and asset division.", icon: "Heart" },
        { title: "Custody & Support", description: "Protecting child interests through custody, support, and visitation advocacy.", icon: "Users" },
        { title: "Protective Orders", description: "Securing safety and legal protection for families in need.", icon: "ShieldCheck" }
      ]
    },
    sijs: {
      title: "Special Immigration Juvenile Status",
      subtitle: "Protecting vulnerable young immigrants.",
      cards: [
        { title: "Youth Advocacy", description: "Representing unmarried, under-21-year-old immigrants in the U.S.", icon: "Users" },
        { title: "Status Eligibility", description: "Legal aid for those abused, neglected, or abandoned by a parent.", icon: "ShieldCheck" },
        { title: "Pathway to Residency", description: "Navigating the SIJS process for a stable legal future.", icon: "Landmark" }
      ]
    },
    traffic: {
      title: "MD/DC/VA Traffic Violations",
      subtitle: "Defending your records and driving privileges.",
      cards: [
        { title: "DUI & DWI", description: "Complex representation for driving under the influence matters.", icon: "Scale" },
        { title: "Driving without a License", description: "Expert defense to mitigate penalties and protect your future.", icon: "Gavel" },
        { title: "Related Violations", description: "Handling important matters related to regional traffic law.", icon: "ShieldCheck" }
      ]
    },
    accidents: {
      title: "Car Accidents",
      subtitle: "Vigorous advocacy for injury compensation.",
      cards: [
        { title: "Insurance Negotiation", description: "Direct negotiation with insurance companies for fair settlements.", icon: "Handshake" },
        { title: "Litigation", description: "Courtroom advocacy to secure compensation for damages.", icon: "Gavel" },
        { title: "Damages & Recovery", description: "Securing medical bills, lost wages, and pain and suffering awards.", icon: "CheckCircle2" }
      ]
    },
    'workers-comp': {
      title: "Workers Compensation",
      subtitle: "Securing your rights after work-related injuries.",
      cards: [
        { title: "Claim Representation", description: "Claims before State Commission and Courts for work injuries.", icon: "Briefcase" },
        { title: "Wage & Medical Care", description: "Recovering medical care, wage replacement, and rehabilitation.", icon: "Award" },
        { title: "Employee Recovery", description: "Dedicated support for those ill or injured due to their job.", icon: "Heart" }
      ]
    },
    'employment-disc': {
      title: "Employment Discrimination",
      subtitle: "Fighting for fair treatment in the workplace.",
      cards: [
        { title: "Wrongful Termination", description: "Representation for those unlawfully dismissed from their roles.", icon: "Gavel" },
        { title: "Harassment & Discrimination", description: "Handling complex sexual harassment and discrimination matters.", icon: "Scale" },
        { title: "Administrative Support", description: "Guidance through administrative processes and wage/hour violations.", icon: "FileText" }
      ]
    },
    military: {
      title: "Military Law",
      subtitle: "Advanced advocacy for our servicemembers.",
      cards: [
        { title: "UCMJ Matters", description: "Representation in courts-martial and administrative actions.", icon: "ShieldCheck" },
        { title: "Record Corrections", description: "Handling appeals and corrections to military records.", icon: "FileText" },
        { title: "Security Clearances", description: "Legal guidance on security clearance issues and administrative matters.", icon: "Lock" }
      ]
    },
    blog: {
      title: "Insights",
      subtitle: "Legal updates for the MD, DC, and VA community.",
      cards: [
        { title: "Tri-State Real Estate 2026", description: "Navigating new tax regulations for property owners.", meta: "Legal Update", icon: "BookOpen" },
        { title: "Small Business Compliance", description: "Essential checklists for Rockville entrepreneurs.", meta: "Business", icon: "BookOpen" },
        { title: "Mediation vs. Litigation", description: "Choosing the right path for your civil dispute.", meta: "Guide", icon: "BookOpen" }
      ]
    },
    cases: {
      title: "Case Experience",
      subtitle: "Highlights of our successful advocacy.",
      cards: [
        { title: "Real Estate Success", description: "Resolved a high-value commercial boundary dispute in DC.", icon: "CheckCircle2" },
        { title: "Business Protection", description: "Successfully defended a Virginia startup against claims.", icon: "CheckCircle2" },
        { title: "Civil Resolution", description: "Mediated a complex inheritance dispute in Maryland.", icon: "CheckCircle2" }
      ]
    },
    faqs: {
      title: "FAQs",
      subtitle: "Answers for prospective clients.",
      cards: [
        { title: "How do I schedule a consultation?", description: "You can schedule a consultation by calling our office directly or by submitting an inquiry through our contact form." },
        { title: "Which areas do you serve?", description: "We are licensed and serve clients in Maryland, Washington D.C., and Virginia." },
        { title: "Are services available in other languages?", description: "No, all legal services are provided strictly in English." }
      ]
    },
    privacy: {
      title: "Privacy Policy",
      subtitle: "Protecting client confidentiality.",
      cards: [
        { title: "Attorney-Client Privilege", description: "We maintain the highest standards of confidentiality." },
        { title: "Data Security", description: "Client data is stored using encrypted legal software." },
        { title: "Information Use", description: "We only collect data necessary for representation." }
      ]
    },
    terms: {
      title: "Terms of Use",
      subtitle: "Guidelines for visitors.",
      cards: [
        { title: "Informational Purpose", description: "Content on this site is not formal legal advice." },
        { title: "No Client Relationship", description: "Using our contact form does not create an attorney-client relationship." },
        { title: "Governing Law", description: "Terms are governed by the laws of the jurisdictions where we are licensed." }
      ]
    },
    cookies: {
      title: "Cookie Policy",
      subtitle: "Your browsing experience.",
      cards: [
        { title: "Usage", description: "We use essential cookies for site functionality." },
        { title: "Analytics", description: "Anonymous data helps us improve our services." },
        { title: "Preferences", description: "We store your preferences for a more personalized visit." }
      ]
    }
  }
};

export const ICON_MAP: Record<string, React.ReactNode> = {
  Briefcase: <Briefcase className="w-8 h-8" />,
  Landmark: <Landmark className="w-8 h-8" />,
  Gavel: <Gavel className="w-8 h-8" />,
  Users: <Users className="w-8 h-8" />,
  Globe: <Globe className="w-8 h-8" />,
  Scale: <Scale className="w-8 h-8" />,
  Handshake: <Handshake className="w-8 h-8" />,
  ShieldCheck: <ShieldCheck className="w-8 h-8" />,
  CheckCircle2: <CheckCircle2 className="w-8 h-8" />,
  Clock: <Clock className="w-8 h-8" />,
  Award: <Award className="w-8 h-8" />,
  BookOpen: <BookOpen className="w-8 h-8" />,
  FileText: <FileText className="w-8 h-8" />,
  UserCheck: <UserCheck className="w-8 h-8" />,
  Heart: <Heart className="w-8 h-8" />,
  Plane: <Plane className="w-8 h-8" />,
  FileSearch: <FileSearch className="w-8 h-8" />,
  Lock: <Lock className="w-8 h-8" />
};
